import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuth from '../hooks/useAuth';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const { login } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Mock login - replace with actual API call
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const data = await response.json();
        login(data.data.token, data.data.user);
        navigate('/');
      } else {
        let data = null;
        try { data = await response.json(); } catch { /* corpo vazio/não-JSON */ }

        if (response.status === 429) {
          setError(data?.message || 'Muitas tentativas de login. Aguarde alguns minutos e tente novamente.');
        } else if (response.status === 401) {
          setError('Credenciais inválidas');
        } else {
          setError(data?.message || 'Erro no servidor. Tente novamente em instantes.');
        }
      }
    } catch (err) {
      setError('Falha no login. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex font-body bg-surface">
      {/* Coluna Esquerda - Branding (Apenas Desktop) */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-gradient-to-br from-primary/90 to-primary flex-col justify-between p-12 overflow-hidden">
        {/* Elementos decorativos */}
        <div className="absolute top-[-10%] left-[-10%] w-96 h-96 bg-white/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-black/10 rounded-full blur-3xl"></div>

        <div className="relative z-10">
          <img src="/LOGO.png" alt="MW.hub Relatorios Personalizados" className="h-10 w-auto brightness-0 invert" />
        </div>

        <div className="relative z-10 text-white mt-auto mb-auto">
          <h1 className="text-4xl lg:text-5xl font-bold font-headline leading-tight mb-6">
            Inteligência de ponta para seus anúncios
          </h1>
          <p className="text-lg text-white/80 max-w-md">
            Gerencie campanhas, extraia relatórios avançados e controle leads com visões de conversão integradas.
          </p>
        </div>

        <div className="relative z-10 text-white/60 text-sm">
          &copy; {new Date().getFullYear()} MW.hub Relatorios Personalizados. Todos os direitos reservados.
        </div>
      </div>

      {/* Coluna Direita - Formulário */}
      <div className="flex-1 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-20 xl:px-32 relative bg-surface">
        <div className="mx-auto w-full max-w-md space-y-8">

          <div className="lg:hidden flex justify-center mb-4">
            <img src="/LOGO.png" alt="MW.hub Relatorios Personalizados" className="h-12 w-auto" />
          </div>

          <div>
            <h2 className="mt-2 text-3xl font-bold text-on-surface font-headline lg:text-left text-center">
              Acesse sua conta
            </h2>
            <p className="mt-2 text-sm text-secondary lg:text-left text-center">
              Bem-vindo de volta! Por favor, insira seus dados.
            </p>
          </div>

          <div className="bg-surface-container-lowest p-8 rounded-2xl shadow-[0_8px_32px_rgba(25,28,30,0.06)] border border-outline-variant/30">
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="rounded-md bg-error-container p-4">
                  <div className="text-sm font-medium text-on-error-container">{error}</div>
                </div>
              )}

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-on-surface-variant">
                  E-mail
                </label>
                <div className="mt-1">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="input transition-all duration-200"
                    placeholder="voce@exemplo.com"
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <label htmlFor="password" className="block text-sm font-medium text-on-surface-variant">
                    Senha
                  </label>
                  <div className="text-sm">
                    <a href="#" className="font-medium text-primary hover:text-primary-focus transition-colors">
                      Esqueceu a senha?
                    </a>
                  </div>
                </div>
                <div className="mt-1">
                  <input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    required
                    value={formData.password}
                    onChange={handleChange}
                    className="input transition-all duration-200"
                    placeholder="••••••••"
                  />
                </div>
              </div>

              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  className="h-4 w-4 text-primary focus:ring-primary/20 border-outline rounded cursor-pointer transition-colors"
                />
                <label htmlFor="remember-me" className="ml-2 block text-sm text-on-surface-variant cursor-pointer">
                  Lembrar de mim
                </label>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full primary-pill-gradient text-white rounded-full py-3 px-4 font-bold flex items-center justify-center shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200 font-headline disabled:opacity-70 disabled:hover:translate-y-0 disabled:hover:shadow-lg disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Entrando...
                    </span>
                  ) : 'Entrar na plataforma'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
